import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Logo from '@/components/base/Logo';
import LanguageSwitcher from '@/components/base/LanguageSwitcher';
import { authApi } from '@/lib/api';
import OverviewTab from './OverviewTab';
import ClientsTab from './ClientsTab';
import TransfersTab from './TransfersTab';
import MessagingTab from './MessagingTab';
import CodesTab from './CodesTab';
import BanksTab from './BanksTab';
import IpTab from './IpTab';
import LogsTab from './LogsTab';
import SettingsTab from './SettingsTab';
import DataTab from './DataTab';

interface AdminPortalProps {
  token: string;
  onLogout: () => void;
}

const GROUPS = [
  {
    key: 'pilotage',
    items: [
      { key: 'overview', icon: 'ri-dashboard-line' },
      { key: 'transfers', icon: 'ri-exchange-dollar-line' },
    ],
  },
  {
    key: 'clients',
    items: [{ key: 'clients', icon: 'ri-group-line' }],
  },
  {
    key: 'security',
    items: [
      { key: 'ips', icon: 'ri-router-line' },
      { key: 'codes', icon: 'ri-lock-password-line' },
    ],
  },
  {
    key: 'messaging',
    items: [{ key: 'messaging', icon: 'ri-mail-send-line' }],
  },
  {
    key: 'audit',
    items: [{ key: 'logs', icon: 'ri-history-line' }],
  },
  {
    key: 'config',
    items: [
      { key: 'banks', icon: 'ri-bank-line' },
      { key: 'settings', icon: 'ri-settings-3-line' },
      { key: 'data', icon: 'ri-delete-bin-6-line' },
    ],
  },
];

export default function AdminPortal({ token, onLogout }: AdminPortalProps) {
  const { t } = useTranslation();
  const [tab, setTab] = useState('overview');
  const [email, setEmail] = useState('');

  useEffect(() => {
    let active = true;
    authApi.me(token).then((res) => {
      if (!active) return;
      if (!res.ok || res.role !== 'admin') {
        onLogout();
        return;
      }
      const admin = res.admin as { email?: string } | undefined;
      setEmail(admin?.email ?? '');
    });
    return () => {
      active = false;
    };
  }, [token, onLogout]);

  const renderTab = () => {
    switch (tab) {
      case 'clients':
        return <ClientsTab token={token} />;
      case 'transfers':
        return <TransfersTab token={token} />;
      case 'messaging':
        return <MessagingTab token={token} />;
      case 'codes':
        return <CodesTab token={token} />;
      case 'banks':
        return <BanksTab token={token} />;
      case 'ips':
        return <IpTab token={token} />;
      case 'logs':
        return <LogsTab token={token} />;
      case 'settings':
        return <SettingsTab token={token} />;
      case 'data':
        return <DataTab token={token} />;
      default:
        return <OverviewTab token={token} onNavigate={setTab} />;
    }
  };

  const navLabel = (key: string) => t(`admin.nav.${key}`);

  return (
    <div className="min-h-[100vh] w-full flex bg-background-100">
      <aside className="hidden lg:flex w-[248px] shrink-0 flex-col bg-foreground-950 h-[100vh] max-h-[100vh] sticky top-0 overflow-hidden">
        <div className="h-16 shrink-0 flex items-center px-5 border-b border-background-50/10">
          <Logo to="/admin" variant="light" size="sm" />
        </div>

        <nav className="flex-1 min-h-0 overflow-y-auto overscroll-contain py-4 px-3" aria-label="admin">
          {GROUPS.map((group) => (
            <div key={group.key} className="mb-4">
              <p className="px-3 mb-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-background-50/35 font-label">
                {t(`admin.group.${group.key}`)}
              </p>
              <ul className="flex flex-col gap-0.5">
                {group.items.map((item) => {
                  const active = tab === item.key;
                  return (
                    <li key={item.key}>
                      <button
                        type="button"
                        onClick={() => setTab(item.key)}
                        className={`w-full flex items-center gap-2.5 h-9 px-3 rounded-lg text-[13px] font-medium font-label transition-colors cursor-pointer whitespace-nowrap ${
                          active
                            ? 'bg-background-50 text-foreground-950 font-semibold'
                            : 'text-background-200/75 hover:bg-background-50/10 hover:text-background-50'
                        }`}
                      >
                        <span className="flex items-center justify-center w-4 h-4 shrink-0">
                          <i className={`${item.icon} text-base`} aria-hidden="true" />
                        </span>
                        {navLabel(item.key)}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>

        <div className="shrink-0 px-3 py-4 border-t border-background-50/10">
          <p className="px-3 text-[10.5px] leading-relaxed text-background-200/45 font-label">
            {t('admin.subtitle')}
          </p>
        </div>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-30 w-full border-b border-background-200 bg-background-50/95 backdrop-blur">
          <div className="w-full px-4 md:px-6 h-16 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="lg:hidden shrink-0">
                <Logo to="/admin" size="sm" showName={false} />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-primary-700 font-label">
                  {t('admin.title')}
                </p>
                <h1 className="text-[15px] font-semibold text-foreground-950 leading-tight truncate font-heading">
                  {navLabel(tab)}
                </h1>
              </div>
            </div>

            <div className="flex items-center gap-2 md:gap-3 shrink-0">
              {email ? (
                <span className="hidden md:inline-flex items-center gap-1.5 h-8 px-2.5 rounded-full border border-background-300 bg-background-100 text-[11.5px] font-semibold text-foreground-700 font-label max-w-[220px]">
                  <span className="flex items-center justify-center w-3.5 h-3.5 shrink-0 text-primary-700">
                    <i className="ri-shield-user-line text-[13px]" aria-hidden="true" />
                  </span>
                  <span className="truncate">{email}</span>
                </span>
              ) : null}
              <LanguageSwitcher />
              <button
                type="button"
                onClick={onLogout}
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-background-300 text-[12.5px] font-semibold text-foreground-700 hover:border-accent-400 hover:text-accent-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                <span className="flex items-center justify-center w-4 h-4">
                  <i className="ri-logout-box-r-line text-base" aria-hidden="true" />
                </span>
                <span className="hidden sm:inline">{t('admin.logout')}</span>
              </button>
            </div>
          </div>

          <div className="lg:hidden w-full overflow-x-auto border-t border-background-200">
            <div className="flex items-center gap-1 px-3 py-2">
              {GROUPS.flatMap((group) => group.items).map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => setTab(item.key)}
                  className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-[11.5px] font-semibold font-label whitespace-nowrap transition-colors cursor-pointer ${
                    tab === item.key
                      ? 'bg-foreground-950 text-background-50'
                      : 'bg-background-100 text-foreground-600 border border-background-200'
                  }`}
                >
                  <span className="flex items-center justify-center w-3.5 h-3.5">
                    <i className={`${item.icon} text-[13px]`} aria-hidden="true" />
                  </span>
                  {navLabel(item.key)}
                </button>
              ))}
            </div>
          </div>
        </header>

        <main className="flex-1 w-full px-4 md:px-6 py-5 md:py-6">{renderTab()}</main>

        <footer className="w-full border-t border-background-200 bg-background-50">
          <div className="w-full px-4 md:px-6 py-3.5 flex flex-col sm:flex-row items-center justify-between gap-2 text-[11.5px] text-foreground-500">
            <p className="font-label">© {new Date().getFullYear()} Monexa — {t('admin.title')}</p>
            <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
              <span className="flex items-center justify-center w-3.5 h-3.5">
                <i className="ri-shield-check-line text-[13px]" aria-hidden="true" />
              </span>
              {t('login.footer.security')}
            </span>
          </div>
        </footer>
      </div>
    </div>
  );
}